// Direction B "Ink Header" booking-confirmation card. Pure-display when
// book_appointment reaches `output-available`. Vendor-static data from
// VendorInfoContext; turn-specific data from part.input + part.output.
//
// Per spec §7.19: datetime renders in vendor_timezone, NOT the source ISO
// offset or the visitor's browser TZ. formatDatetime is the single source
// of truth.
//
// Per spec §3.5 + Spike 2 footgun #1: tool outputs may arrive as either a
// dict (chat-only path) or a json.dumps string (over-the-wire path).
// parseToolOutput normalizes both shapes.

import { useVendorInfo } from '../../context/VendorInfoContext'
import { parseToolOutput } from '../../lib/parse-tool-output'
import { formatDatetime } from '../../lib/format-datetime'
import { bookingStatusLabel, whereValue } from '../../lib/booking-labels'
import { ActionCard } from './ActionCard'

export interface BookingConfirmationCardProps {
  input: Record<string, unknown>
  output: unknown
}

export function BookingConfirmationCard({
  input,
  output,
}: BookingConfirmationCardProps) {
  const vendor = useVendorInfo()
  const parsed = parseToolOutput(output) ?? {}
  // RCA Change 1: render the card off the RESULT, not the attempt. book_appointment
  // returns {status:'confirmed'} on success and {status:'error'} on a conflict/failure.
  // Allowlist the success status (not just deny 'error') so a malformed/empty output
  // or any future non-success status also renders nothing — the bot's prose carries it.
  if (parsed.status !== 'confirmed') return null
  const dt = (parsed.appointment_datetime as string) || ''
  const email = (parsed.attendee_email as string) || ''
  // guest_count: prefer output (booking response) but fall back to input (tool arg)
  // — the booking confirmation tool may echo it in either shape.
  const guests =
    (parsed.guest_count as string) ||
    (input?.guest_count as string) ||
    ''
  const formatted = formatDatetime(dt, vendor.vendor_timezone)

  // Build rows defensively — the design's three rows are When / Party / Where,
  // but if the LLM omitted guest_count we hide that row rather than ship "—".
  const rows = [] as { label: string; text: React.ReactNode }[]
  if (formatted) rows.push({ label: 'When', text: formatted })
  if (guests) rows.push({ label: 'Party', text: `${guests} guests` })
  rows.push({ label: 'Where', text: whereValue(parsed, vendor.business_name) })

  const label = bookingStatusLabel(vendor.booking_noun, 'confirmed')

  return (
    <ActionCard
      testId="booking-card"
      status="success"
      eyebrow={label}
      title={label}
      rows={rows}
      foot={
        email ? (
          <>
            Calendar invite sent to <b>{email}</b>
          </>
        ) : null
      }
    />
  )
}
